import { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import Header from "../../Components/Header/Header";
import Dropdown from "../../Components/Dropdown/Dropdown";
import DatePicker from "../../Components/DatePicker/DatePicker";
import BarChart from "../../Components/Charts/BarChart";
import { pageLabel } from "../../Functions/pageLabels";
import "./ActivityLog.css";

// Format milliseconds as a compact "Xm Ys" / "Ys" string.
const fmtDuration = (ms: number) => {
    if (!ms || ms < 1000) return ms ? "<1s" : "—";
    const s = Math.round(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${sec}s`;
    return `${sec}s`;
};

const ActivityLog = ({ data, socket }: { data: any; socket: any }) => {
    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const base = process.env.REACT_APP_BACKEND_URL;

    const [orgs, setOrgs] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [logs, setLogs] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({ school: "", userId: "", day: "", startDate: "", endDate: "" });
    const [limit, setLimit] = useState(25);

    // Organisations for the org dropdown.
    useEffect(() => {
        fetch(`${base}/api/activity/organisations`).then((r) => r.json())
            .then((res) => { if (res.success) setOrgs(res.organisations || []); }).catch(() => {});
    }, [base]);

    // Users (scoped to the selected organisation).
    useEffect(() => {
        const qs = filters.school ? `?school=${encodeURIComponent(filters.school)}` : "";
        fetch(`${base}/api/activity/users${qs}`).then((r) => r.json())
            .then((res) => { if (res.success) setUsers(res.users || []); }).catch(() => {});
    }, [base, filters.school]);

    const queryString = useCallback((extra: Record<string, string> = {}) => {
        const p = new URLSearchParams();
        if (filters.school) p.set("school", filters.school);
        if (filters.userId) p.set("userId", filters.userId);
        if (filters.day) p.set("day", filters.day);
        if (filters.startDate) p.set("startDate", filters.startDate);
        if (filters.endDate) p.set("endDate", filters.endDate);
        Object.entries(extra).forEach(([k, v]) => p.set(k, v));
        return p.toString();
    }, [filters]);

    const load = useCallback(async (p: number) => {
        setLoading(true);
        try {
            const [logsRes, statsRes] = await Promise.all([
                fetch(`${base}/api/activity/logs?${queryString({ page: String(p), limit: String(limit) })}`).then((r) => r.json()),
                fetch(`${base}/api/activity/stats?${queryString()}`).then((r) => r.json()),
            ]);
            if (logsRes.success) { setLogs(logsRes.logs || []); setTotal(logsRes.total || 0); setPage(p); }
            if (statsRes.success) setStats(statsRes);
        } catch (e) { /* ignore */ } finally { setLoading(false); }
    }, [base, queryString, limit]);

    useEffect(() => { load(1); }, [load]);

    const orgOptions = useMemo(() => [
        { value: "", label: en ? "All organisations" : "Toate organizațiile" },
        ...orgs.map((o) => ({ value: o.name, label: `${o.name} (${o.userCount})` })),
    ], [orgs, en]);

    const userOptions = useMemo(() => [
        { value: "", label: en ? "All users" : "Toți utilizatorii" },
        ...users.map((u) => ({ value: u.userId, label: u.fullName?.trim() || u.username || u.userId })),
    ], [users, en]);

    const fmtDate = (d: string) => new Date(d).toLocaleDateString(en ? "en-GB" : "ro-RO");
    const fmtTime = (d: string) => new Date(d).toLocaleTimeString(en ? "en-GB" : "ro-RO");
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const perDayData = useMemo(() => (stats?.perDay || []).map((r: any) => ({
        label: new Date(r.day).toLocaleDateString(en ? "en-GB" : "ro-RO", { day: "2-digit", month: "2-digit" }),
        value: Number(r.count),
    })), [stats, en]);

    const byHourData = useMemo(() => {
        const map = new Map((stats?.byHour || []).map((r: any) => [Number(r.hour), Number(r.count)]));
        return Array.from({ length: 24 }, (_, h) => ({ label: `${h}`, value: Number(map.get(h) || 0) }));
    }, [stats]);

    const topPageMax = Math.max(1, ...((stats?.topPages || []).map((p: any) => Number(p.count))));

    return (
        <div className="content activity-log">
            <Header data={data} socket={socket} />
            <h2 className="title">{en ? "Activity log" : "Jurnal de activitate"}</h2>

            <div className="al-filters">
                <Dropdown label={en ? "Organisation" : "Organizație"} value={filters.school} options={orgOptions}
                    onChange={(v) => setFilters((f) => ({ ...f, school: v, userId: "" }))} />
                <Dropdown label={en ? "User" : "Utilizator"} value={filters.userId} options={userOptions}
                    onChange={(v) => setFilters((f) => ({ ...f, userId: v }))} />
                <DatePicker label={en ? "Day" : "Zi"} value={filters.day} en={en}
                    onChange={(v) => setFilters((f) => ({ ...f, day: v, startDate: "", endDate: "" }))} />
                <DatePicker label={en ? "From" : "De la"} value={filters.startDate} en={en}
                    onChange={(v) => setFilters((f) => ({ ...f, startDate: v, day: "" }))} />
                <DatePicker label={en ? "To" : "Până la"} value={filters.endDate} en={en}
                    onChange={(v) => setFilters((f) => ({ ...f, endDate: v, day: "" }))} />
                <button className="al-clear" onClick={() => setFilters({ school: "", userId: "", day: "", startDate: "", endDate: "" })}>
                    {en ? "Clear" : "Resetează"}
                </button>
            </div>

            {/* Stat tiles */}
            <div className="al-tiles">
                <div className="al-tile"><span className="al-tile-val">{stats?.summary?.totalEvents ?? 0}</span><span className="al-tile-lbl">{en ? "Events" : "Evenimente"}</span></div>
                <div className="al-tile"><span className="al-tile-val">{stats?.summary?.uniqueUsers ?? 0}</span><span className="al-tile-lbl">{en ? "Users" : "Utilizatori"}</span></div>
                <div className="al-tile"><span className="al-tile-val">{stats?.summary?.schools ?? 0}</span><span className="al-tile-lbl">{en ? "Organisations" : "Organizații"}</span></div>
                <div className="al-tile"><span className="al-tile-val">{fmtDuration(Number(stats?.summary?.avgTimeMs || 0))}</span><span className="al-tile-lbl">{en ? "Avg / page" : "Medie / pagină"}</span></div>
                <div className="al-tile"><span className="al-tile-val">{fmtDuration(Number(stats?.summary?.totalTimeMs || 0))}</span><span className="al-tile-lbl">{en ? "Total time" : "Timp total"}</span></div>
            </div>

            {/* Charts */}
            <div className="al-charts">
                <div className="al-card">
                    <h3>{en ? "Activity per day" : "Activitate pe zi"}</h3>
                    <BarChart data={perDayData} />
                </div>
                <div className="al-card">
                    <h3>{en ? "Activity by hour" : "Activitate pe ore"}</h3>
                    <BarChart data={byHourData} />
                </div>
            </div>

            <div className="al-charts">
                <div className="al-card">
                    <h3>{en ? "Top pages" : "Pagini de top"}</h3>
                    <div className="al-toplist">
                        {(stats?.topPages || []).map((p: any, i: number) => (
                            <div className="al-topitem" key={i}>
                                <span className="al-topname" title={p.path}>{pageLabel(p.path, en)}</span>
                                <div className="al-topbartrack"><div className="al-topbar" style={{ width: `${(Number(p.count) / topPageMax) * 100}%` }} /></div>
                                <span className="al-topcount">{p.count}</span>
                                <span className="al-topavg">{fmtDuration(Number(p.avgTimeMs || 0))}</span>
                            </div>
                        ))}
                        {!stats?.topPages?.length ? <div className="al-empty">—</div> : null}
                    </div>
                </div>
                <div className="al-card">
                    <h3>{en ? "Most active users" : "Cei mai activi utilizatori"}</h3>
                    <div className="al-toplist">
                        {(stats?.topUsers || []).map((u: any, i: number) => (
                            <div className="al-topitem" key={i}>
                                <span className="al-topname">{u.fullName?.trim() || u.username || u.userId}</span>
                                <div className="al-topbartrack"><div className="al-topbar" style={{ width: `${(Number(u.count) / Math.max(1, Number(stats?.topUsers?.[0]?.count || 1))) * 100}%` }} /></div>
                                <span className="al-topcount">{u.count}</span>
                            </div>
                        ))}
                        {!stats?.topUsers?.length ? <div className="al-empty">—</div> : null}
                    </div>
                </div>
            </div>

            <div className="al-tablebar">
                <span className="al-meta">{loading ? (en ? "Loading…" : "Se încarcă…") : `${total} ${en ? "entries" : "înregistrări"}`}</span>
                <div className="al-perpage">
                    <span className="al-perpage-lbl">{en ? "Rows" : "Rânduri"}</span>
                    <Dropdown
                        value={String(limit)}
                        options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                        onChange={(v) => setLimit(Number(v))}
                    />
                </div>
            </div>

            <div className="al-table">
                <div className="al-row al-head">
                    <div>{en ? "User" : "Utilizator"}</div>
                    <div>{en ? "Organisation" : "Organizație"}</div>
                    <div>{en ? "Page" : "Pagină"}</div>
                    <div>{en ? "Time spent" : "Timp petrecut"}</div>
                    <div>{en ? "Date" : "Data"}</div>
                    <div>{en ? "Hour" : "Ora"}</div>
                </div>
                {logs.map((l) => (
                    <div className="al-row" key={l.id}>
                        <div title={l.username}>{l.fullName || l.username || l.userId}</div>
                        <div className="al-muted">{l.school || "—"}</div>
                        <div className="al-path" title={l.path}>{pageLabel(l.path, en)}</div>
                        <div>{fmtDuration(Number(l.durationMs || 0))}</div>
                        <div>{fmtDate(l.createdAt)}</div>
                        <div>{fmtTime(l.createdAt)}</div>
                    </div>
                ))}
                {!loading && !logs.length ? <div className="al-empty">{en ? "No activity found" : "Nicio activitate găsită"}</div> : null}
            </div>

            <div className="al-pager">
                <button disabled={page <= 1} onClick={() => load(page - 1)}>‹</button>
                <span>{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => load(page + 1)}>›</button>
            </div>
        </div>
    );
};

export default ActivityLog;
